import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';

import { ref, watch } from 'vue';
import _sortBy from 'lodash/sortBy';

export type HeadersData = {
  value: string;
  title: string;
  colspan?: number;
  isNotSortable?: boolean;
  className?: string;
}[];

export interface UseTableComposable {
  sortedData: Ref<ReadonlyDeep<unknown[]> | unknown[]>;
  actives: Ref<Record<string, boolean>>;
  ascs: Ref<Record<string, boolean>>;
  handleSort: (titleName: string, event: Event) => void;
}

export interface Props {
  data?: ReadonlyDeep<unknown[]> | unknown[];
}

export default function useTable(props: Props): UseTableComposable {
  const sortedData = ref<ReadonlyDeep<unknown[]> | unknown[]>([]);
  const actives = ref<Record<string, boolean>>({});
  const ascs = ref<Record<string, boolean>>({});

  watch(
    () => props.data,
    (newValues, oldValues) => {
      sortedData.value = getSortedData(newValues);
    },
    { immediate: true },
  );

  function getSortedData(
    newData?: ReadonlyDeep<unknown[]> | unknown[],
  ): ReadonlyDeep<unknown[]> | unknown[] {
    const titleName = Object.keys(actives.value)[0];
    if (!titleName) {
      if (!newData) return [];
      return [...newData];
    }
    const isAsc = ascs.value[titleName];
    const newSortedData = _sortBy(newData, [titleName]);
    if (!isAsc) newSortedData.reverse();
    return newSortedData;
  }

  function handleSort(titleName: string, event: Event): void {
    event.stopPropagation();
    if (actives.value[titleName] && !ascs.value[titleName]) {
      actives.value = {};
      ascs.value = {};
      sortedData.value = getSortedData(props.data);
    } else {
      actives.value = { [titleName]: true };
      if (ascs.value[titleName]) {
        ascs.value = { [titleName]: false };
        sortedData.value = getSortedData(props.data);
      } else {
        ascs.value = { [titleName]: true };
        sortedData.value = getSortedData(props.data);
      }
    }
  }

  return {
    sortedData,
    actives,
    ascs,
    handleSort,
  };
}
